import React, { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Phone, Lock, User } from "lucide-react";
import { BRAND } from "@/lib/brand";

export default function Login() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(phone.trim(), password);
        toast.success("Welcome back");
      } else {
        await register(phone.trim(), password, name.trim() || "Owner");
        toast.success("Account created");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = () => {
    setPhone("9999999999");
    setPassword("admin123");
    setMode("login");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 grain-bg">
      {/* Left — clean brand panel (no logo / no large brand caption) */}
      <div
        className="hidden lg:flex relative p-14 flex-col text-white"
        style={{
          background:
            "linear-gradient(135deg, #2C2A29 0%, #5A453E 55%, #9E6D5B 100%)",
        }}
      >
        <div className="flex-1 flex items-center">
          <div className="max-w-md fade-up">
            <p className="text-xs tracking-[0.32em] uppercase opacity-75 mb-5">
              Cosmetic back-office
            </p>
            <h2 className="font-heading text-4xl xl:text-5xl leading-[1.1] mb-6 tracking-tight">
              Know what sold.
              <br />
              Reorder what matters.
            </h2>
            <p className="text-base leading-relaxed opacity-85 max-w-sm">
              Run your shops from one quiet command center — sales, stock and credits, all in one place.
            </p>
          </div>
        </div>
        <p className="text-xs opacity-60 tracking-wide">
          © {new Date().getFullYear()} · All rights reserved
        </p>
      </div>

      {/* Right — clean sign-in form with brand mark */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <Card className="w-full max-w-md p-10 shadow-sm fade-up" data-testid="auth-card">
          <div className="flex flex-col items-center mb-8" data-testid="auth-brand">
            <img
              src={BRAND.logoUrl}
              alt={BRAND.name}
              className="h-16 w-auto select-none"
              draggable={false}
            />
            <p
              className="mt-2 text-xs font-medium tracking-[0.32em] uppercase text-muted-foreground"
              data-testid="auth-brand-caption"
            >
              Intel Hitman
            </p>
          </div>

          <div className="mb-7 text-center">
            <h2 className="font-heading text-3xl tracking-tight">
              {mode === "login" ? "Sign in" : "Create an account"}
            </h2>
            <p className="text-sm text-muted-foreground mt-2">
              {mode === "login" ? "Use your phone and password." : "You'll use phone + password to sign in."}
            </p>
          </div>

          <form onSubmit={submit} className="space-y-5">
            {mode === "register" && (
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-wider text-muted-foreground">
                  Your name
                </Label>
                <div className="relative mt-1.5">
                  <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="name"
                    className="pl-9 h-11"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Shop owner"
                    data-testid="register-name-input"
                  />
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="phone" className="text-xs uppercase tracking-wider text-muted-foreground">
                Phone
              </Label>
              <div className="relative mt-1.5">
                <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="phone"
                  className="pl-9 h-11 font-mono-data"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="9999999999"
                  required
                  data-testid="auth-phone-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">
                Password
              </Label>
              <div className="relative mt-1.5">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  className="pl-9 h-11"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="auth-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-11 mt-2"
              style={{ backgroundColor: "#9E6D5B" }}
              disabled={loading}
              data-testid="auth-submit-btn"
            >
              {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <div className="mt-7 flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-muted-foreground hover:text-foreground transition-colors"
              data-testid="toggle-auth-mode-btn"
            >
              {mode === "login" ? "Need an account? Register" : "Have an account? Sign in"}
            </button>
            <button
              type="button"
              onClick={fillDemo}
              className="text-xs tracking-wider uppercase text-muted-foreground hover:text-foreground transition-colors"
              data-testid="fill-demo-btn"
            >
              Use demo
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
}
